import { authFetch } from './authFetch'
import { authUrl } from './endpoints'

export type AdminUserSummary = {
  id: string
  short_id: string
  email: string
  display_name: string
  is_active: boolean
  last_login_at: string | null
  created_at: string
  roles: Array<{ role: string; resource_type: string | null; resource_id: string | null }>
}

export type AdminUserDetail = AdminUserSummary

export type RoleRow = {
  id: string
  name: string
  description: string | null
  is_system: boolean
  operator_id: string | null
}

export type GrantInput = {
  role: string
  resource_type: 'organisation' | 'operator' | 'stream' | null
  resource_id: string | null
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await authFetch(authUrl(path), {
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  })
  if (!res.ok) {
    const err = new Error(`${init.method ?? 'GET'} ${path} → ${res.status}`) as Error & {
      status: number
      detail?: unknown
    }
    err.status = res.status
    try { err.detail = await res.json() } catch { /* ignore */ }
    throw err
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const adminClient = {
  listUsers: (params: { includeInactive?: boolean } = {}) => {
    const qs = new URLSearchParams()
    if (params.includeInactive) qs.set('includeInactive', 'true')
    const suffix = qs.toString() ? `?${qs}` : ''
    return request<{ users: AdminUserSummary[] }>(`/auth/users${suffix}`)
  },

  getUser: (id: string) => request<AdminUserDetail>(`/auth/users/${id}`),

  createUser: (body: {
    email: string
    display_name: string
    password: string
    is_active?: boolean
    roles?: GrantInput[]
  }) =>
    request<{ id: string; email: string; display_name: string; is_active: boolean }>(
      '/auth/users',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  updateUser: (id: string, body: { display_name?: string; password?: string; is_active?: boolean }) =>
    request<{ id: string; email: string; display_name: string; is_active: boolean }>(
      `/auth/users/${id}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),

  deleteUser: (id: string) =>
    request<{ status: string }>(`/auth/users/${id}`, { method: 'DELETE' }),

  listUserRoles: (id: string) =>
    request<{
      roles: Array<{
        id: string
        role_name: string
        resource_type: string | null
        resource_id: string | null
        granted_at: string
        expires_at: string | null
      }>
    }>(`/auth/users/${id}/roles`),

  grantUserRole: (id: string, grant: GrantInput) =>
    request<{ id: string }>(`/auth/users/${id}/roles`, {
      method: 'POST',
      body: JSON.stringify(grant),
    }),

  revokeUserRole: (id: string, grantId: string) =>
    request<{ status: string }>(`/auth/users/${id}/roles/${grantId}`, {
      method: 'DELETE',
    }),

  listRoles: () => request<{ roles: RoleRow[] }>('/auth/roles'),
}
